import * as React from 'react';
import classNames from 'clsx';
import { WithStyles } from '@material-ui/styles';
import { blue, cyan, common, indigo } from '@material-ui/core/colors';
import { ViewState } from '@devexpress/dx-react-scheduler';

import {
    Scheduler,
    Appointments,
    MonthView,
    WeekView,
    DayView,
    Resources,
    Toolbar,
    ViewSwitcher,
    AppointmentTooltip,
    DateNavigator,
    TodayButton
} from '@devexpress/dx-react-scheduler-material-ui';

import {
    Paper,
    withStyles,
    Theme,
    createStyles,
    Grid,
    Typography
} from '@material-ui/core';

import { fade } from '@material-ui/core/styles/colorManipulator';

const styles = ({ palette }: Theme) => createStyles({
    appointment1: {
        borderRadius: 8,
        borderBottom: 8,
        textDecorationColor: common.black,
        backgroundColor: blue[400],
        '&:hover': {
            backgroundColor: blue[300],
        },
    },
    appointment2: {
        borderRadius: 8,
        borderBottom: 8,
        backgroundColor: blue[500],
        '&:hover': {
            backgroundColor: blue[300],
        },
    },
    appointment3: {
        borderRadius: 8,
        borderBottom: 8,
        textDecorationColor: common.black,
        backgroundColor: cyan[500],
        '&:hover': {
            backgroundColor: cyan[200],
        },
    },
    corretiva: {
        borderLeft: `12px solid ${indigo[500]}`,
    },
    avcb: {
        borderLeft: `12px solid ${indigo[500]}`,
    },
    preventiva: {
        borderLeft: `12px solid ${indigo[500]}`,
    },
    pendencia: {
        borderLeft: `12px solid ${indigo[500]}`,
    },
    weekEndCell: {
        backgroundColor: fade(palette.action.disabledBackground, 0.09),
        '&:hover': {
            backgroundColor: fade(palette.action.disabledBackground, 0.09),
        },
        '&:focus': {
            backgroundColor: fade(palette.action.disabledBackground, 0.09),
        },

    },
    weekEndDayScaleCell: {
        backgroundColor: fade(palette.action.disabledBackground, 0.09),

    },
    dayScaleCell: {
        backgroundColor: fade(palette.action.disabledBackground, 0.09),

    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: common.white,
        size: '18px'
    },
    content: {
        opacity: 1.0,
        whiteSpace: 'nowrap',
        color: common.white
    },
    container: {
        width: '100%',
        height: '100%',
        margin: 0,
    },
    compress: {
        height: 160
    },
    titleContainer: {
        paddingBottom: 10,
    },
    dateAndTitle: {
        fontWeight: '20px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: 20,
    },
});

type AppointmentProps = Appointments.AppointmentProps & WithStyles<typeof styles>;
type AppointmentContentProps = Appointments.AppointmentContentProps & WithStyles<typeof styles>;
type TimeTableCellProps = MonthView.TimeTableCellProps & WithStyles<typeof styles>;
type DayScaleCellProps = MonthView.DayScaleCellProps & WithStyles<typeof styles>;

const isWeekEnd = (date) => date.getDay() === 0 || date.getDay() === 6;

const DayScaleCell = withStyles(styles)(({ startDate, classes, ...restProps }: DayScaleCellProps) => (
    <MonthView.DayScaleCell
        className={classNames({
            [classes.weekEndDayScaleCell]: isWeekEnd(startDate)
        })}
        startDate={startDate}
        {...restProps}
    />
));

const TimeTableCell = withStyles(styles)(({ startDate, classes, ...restProps }: TimeTableCellProps) => (
    <MonthView.TimeTableCell
        className={classNames({
            [classes.weekEndCell]: isWeekEnd(startDate),
            [classes.compress]: true
        })}
        startDate={startDate}
        {...restProps}
    />
));

const Appointment = withStyles(styles)(({ classes, data, ...restProps }: AppointmentProps) => (
    <Appointments.Appointment
        {...restProps}
        className={classNames({
            [classes.corretiva]: data.priority === 1,
            [classes.avcb]: data.priority === 2,
            [classes.preventiva]: data.priority === 3,
            [classes.pendencia]: data.priority === 4,
            [classes.appointment1]: data.selected == 0,
            [classes.appointment2]: data.selected == 1,
            [classes.appointment3]: data.selected == 2
        })}
        data={data}
    />
));

const AppointmentContent = withStyles(styles, { name: 'AppointmentContent' })(({ classes, data, ...restProps }: AppointmentContentProps) => {
    return (
        <Appointments.AppointmentContent {...restProps} data={data}>
            <div className={classes.container}>
                <div className={classes.text}>
                    {`Ocorrência: ${data.number}`}
                </div>
                <div className={classNames(classes.text, classes.content)}>
                    {`Tipo: ${data.type}`}
                </div>
                <div className={classNames(classes.text, classes.content)}>
                    {`Centro de Custo: ${data.cost}`}
                </div>
                <div className={classNames(classes.text, classes.content)}>
                    {`Unidade: ${data.unit}`}
                </div>
                <div className={classNames(classes.text, classes.content)}>
                    {`Técnico: ${(data.technician) ? data.technician : ''}`}
                </div>
            </div>
        </Appointments.AppointmentContent>
    );
});

const Header = (({
    children, appointmentData, ...restProps
}) => (
    <Typography variant='h3' style={{ margin: '20px' }} >
        Ocorrência: {appointmentData.number}
    </Typography>
));

const ServiceMap = ({ date, appointments, size }) => {

    const getResources = (appointments) => {
        var resources = [
            {
                fieldName: 'type',
                title: 'Tipo',
                instances: appointments.map((apt) => {
                    return ({
                        id: apt.type,
                        text: 'Tipo: ' + apt.type,
                        color: (apt.priority === 1) ? (indigo[500]) : (apt.priority === 2) ? (indigo[500]) : (apt.priority === 3) ? (indigo[500]) : (indigo[500])
                    });

                })
            },
            {
                fieldName: 'id',
                title: 'Unidade',
                instances: appointments.map((apt) => {
                    return ({
                        id: apt.id,
                        text: 'Unidade: ' + apt.cost + ' - ' + apt.unit,
                        color: (apt.priority === 1) ? (indigo[500]) : (apt.priority === 2) ? (indigo[500]) : (apt.priority === 3) ? (indigo[500]) : (indigo[500])
                    });

                })
            },
            {
                fieldName: 'technician',
                title: 'Técnico',
                instances: appointments.map((apt) => {
                    return ({
                        id: apt.technician,
                        text: 'Técnico: ' + apt.technician
                    });

                })
            },
        ];
        return resources;
    }

    return (

        <Scheduler
            data={appointments}
            height={size}
            locale="pt-BR"
        >
            <ViewState
                defaultCurrentDate={new Date(date)}
                defaultCurrentViewName="Week"
            />

            <DayView startDayHour={7} endDayHour={22} displayName='Dia' />

            <WeekView startDayHour={7} endDayHour={22} displayName='Semana' />

            <MonthView
                displayName='Mês'
                dayScaleCellComponent={DayScaleCell}
                timeTableCellComponent={TimeTableCell}
                intervalCount={1}
            />

            <Toolbar />
            <DateNavigator />
            <TodayButton displayName='Hoje' />
            <ViewSwitcher />

            <Appointments
                appointmentComponent={Appointment}
                appointmentContentComponent={AppointmentContent}
            />

            <Resources
                data={getResources(appointments)}
            />

            <AppointmentTooltip
                showCloseButton
            />

        </Scheduler>

    );
};

export default ServiceMap;